import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Spinner, Alert, ProgressBar } from 'react-bootstrap';
import api from '../services/api';
import { FiCheckCircle, FiClock, FiUsers, FiUserX, FiCalendar, FiAward } from 'react-icons/fi';

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await api.get('/dashboard/stats');
                setStats(response.data);
            } catch (err) {
                console.error("Erro ao carregar dashboard:", err);
                const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Erro desconhecido';
                const errorDetails = err.response?.data?.details ? ` Detalhes: ${err.response.data.details}` : '';
                setError(`Não foi possível carregar os dados: ${errorMessage}${errorDetails}`);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    if (!stats) return null;

    // Card Component for Metrics
    const StatCard = ({ title, value, icon, color, subtext }) => (
        <Card className="h-100 shadow-sm border-0" style={{ backgroundColor: '#F5F7FA' }}>
            <Card.Body className="d-flex align-items-center">
                <div className="rounded-circle p-3 me-3 d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: 'white', color: color, width: '60px', height: '60px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    {icon}
                </div>
                <div>
                    <h6 className="text-muted text-uppercase small fw-bold mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>{title}</h6>
                    <h3 className="fw-bold mb-0" style={{ fontFamily: 'Nunito, sans-serif', color: '#2c3e50' }}>{value}</h3>
                    {subtext && <small className="text-muted">{subtext}</small>}
                </div>
            </Card.Body>
        </Card>
    );

    return (
        <Container className="py-4">
            <h2 className="mb-4 fw-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#006064' }}>Dashboard</h2>

            {/* First Row: Counters */}
            <Row className="g-4 mb-4">
                <Col md={6} lg={4}>
                    <StatCard
                        title="Treinamentos Concluídos"
                        value={stats.total_concluded}
                        icon={<FiCheckCircle size={28} />}
                        color="#00BFA5" // Turquesa Digital
                    />
                </Col>
                <Col md={6} lg={4}>
                    <StatCard
                        title="Treinamentos Pendentes"
                        value={stats.total_pending}
                        icon={<FiClock size={28} />}
                        color="#FFD54F" // Amarelo Solar
                    />
                </Col>
                <Col md={6} lg={4}>
                    <StatCard
                        title="Faltas Registradas"
                        value={stats.absences}
                        icon={<FiUserX size={28} />}
                        color="#FF8A65" // Coral Vibrante
                    />
                </Col>
            </Row>

            {/* Second Row: Rates */}
            <Row className="g-4 mb-5">
                <Col md={6}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Body>
                            <div className="d-flex align-items-center mb-3">
                                <FiUsers className="me-2" size={24} style={{ color: '#00BFA5' }} />
                                <h5 className="fw-bold mb-0" style={{ fontFamily: 'Nunito, sans-serif' }}>Taxa de Presença</h5>
                            </div>
                            <h2 className="display-4 fw-bold mb-2" style={{ color: '#00BFA5' }}>{stats.attendance_rate}%</h2>
                            <ProgressBar now={stats.attendance_rate} variant="success" style={{ height: '8px', backgroundColor: '#e9ecef' }} />
                            <small className="text-muted mt-2 d-block">% de participantes presentes nos treinamentos</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Body>
                            <div className="d-flex align-items-center mb-3">
                                <FiUserX className="me-2" size={24} style={{ color: '#FF8A65' }} />
                                <h5 className="fw-bold mb-0" style={{ fontFamily: 'Nunito, sans-serif' }}>Taxa de Faltas</h5>
                            </div>
                            <h2 className="display-4 fw-bold mb-2" style={{ color: '#FF8A65' }}>{stats.absence_rate}%</h2>
                            <ProgressBar now={stats.absence_rate} variant="danger" style={{ height: '8px', backgroundColor: '#e9ecef' }} />
                            <small className="text-muted mt-2 d-block">% de participantes que não compareceram aos treinamentos</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="g-4">
                {/* Next Trainings */}
                <Col lg={7}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
                            <div className="d-flex align-items-center">
                                <FiCalendar className="me-2 text-primary" />
                                <h5 className="fw-bold mb-0" style={{ fontFamily: 'Nunito, sans-serif' }}>Próximos Treinamentos</h5>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Table hover responsive className="mb-0 align-middle">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="border-0 text-muted small fw-bold text-uppercase ps-3">Treinamento</th>
                                        <th className="border-0 text-muted small fw-bold text-uppercase">Data/Hora</th>
                                        <th className="border-0 text-muted small fw-bold text-uppercase text-center">Inscritos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.next_trainings.length > 0 ? (
                                        stats.next_trainings.map((t) => (
                                            <tr key={t.id}>
                                                <td className="ps-3 fw-bold text-dark">{t.titulo}</td>
                                                <td className="text-muted">
                                                    {new Date(t.data).toLocaleDateString()} <small>{new Date(t.data).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
                                                </td>
                                                <td className="text-center">
                                                    <span className="badge bg-light text-dark border">{t.participantes}</span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3" className="text-center py-4 text-muted">Nenhum treinamento agendado.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Ranking */}
                <Col lg={5}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
                            <div className="d-flex align-items-center">
                                <FiAward className="me-2 text-warning" />
                                <h5 className="fw-bold mb-0" style={{ fontFamily: 'Nunito, sans-serif' }}>Ranking de Assiduidade</h5>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Table hover responsive className="mb-0 align-middle">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="border-0 text-muted small fw-bold text-uppercase ps-3">Colaborador</th>
                                        <th className="border-0 text-muted small fw-bold text-uppercase text-end pe-3">Presenças</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.ranking.length > 0 ? (
                                        stats.ranking.map((r, index) => (
                                            <tr key={index}>
                                                <td className="ps-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className={`rounded-circle d-flex align-items-center justify-content-center me-2 ${index < 3 ? 'fw-bold text-white' : 'text-muted bg-light'}`}
                                                            style={{
                                                                width: '24px',
                                                                height: '24px',
                                                                fontSize: '0.8rem',
                                                                backgroundColor: index === 0 ? '#FFD54F' : index === 1 ? '#B0BEC5' : index === 2 ? '#FFAB91' : '#f8f9fa'
                                                            }}>
                                                            {index + 1}
                                                        </div>
                                                        <span className="fw-bold text-dark">{r.nome}</span>
                                                    </div>
                                                </td>
                                                <td className="text-end pe-3 fw-bold text-success">{r.presencas}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="2" className="text-center py-4 text-muted">Nenhum dado de presença.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Dashboard;
